import { inject, Injectable } from '@angular/core';
import { LogLevel, TelemetrySink } from '@pcp/logging';
import { IPcpApplicationInsights, PcpApplicationInsights } from '../types/application-insights-interface';

/**
 * Application Insights' own severity scale (0 = verbose … 4 = critical).
 *
 * It lives here rather than in `@pcp/logging`, which must stay free of anything vendor-specific.
 * The logger decides *which* sink method to call; this decides how to express the severity.
 */
const AI_SEVERITY: Record<LogLevel, number> = {
    // Application Insights has five levels to our six, so two must collapse. Verbose and debug
    // are both "diagnostic detail" and lose least by sharing 0; debug-vs-info is the more
    // meaningful boundary, so info keeps Information (1) to itself.
    [LogLevel.VERBOSE]: 0,
    [LogLevel.DEBUG]: 0,
    [LogLevel.INFO]: 1,
    [LogLevel.WARNING]: 2,
    [LogLevel.ERROR]: 3,
    [LogLevel.CRITICAL]: 4,
};

/**
 * Sends the logger's output to Application Insights.
 *
 * Registered by `provideInsights()` as `PCP_TELEMETRY_SINK`. Applies **no filtering of its own** —
 * the severity floors live in the logger, and the SDK filters nothing either, so whatever arrives
 * here is sent.
 *
 * ## Properties are passed inside `baseData`, so everything is a dimension
 *
 * Note `trackEvent({ name, properties })` and **not** `trackEvent({ name }, properties)`. The two
 * are not equivalent: the second argument lands in `telemetryItem.data`, which the SDK runs through
 * `_extractPropsAndMeasurements` and splits on `isNumber(value)` — numbers to `customMeasurements`,
 * everything else to `customDimensions`. Inside `baseData`, `EventEnvelopeCreator` copies the bag
 * verbatim (`applicationinsights-web.js:13519-13534`, 3.4.4).
 *
 * The verbatim form is deliberate, decided 2026-09-29. The SDK's split is a rule about JavaScript
 * *types*, not about meaning, and it puts every numeric id in the aggregation bag — `avg(moduleId)`
 * is meaningless, and `layer`, `slideId` and `moduleId` are all identifiers. Dimensions are where
 * they belong.
 *
 * ⚠️ The cost was accepted knowingly: a genuinely aggregatable number loses its bag too.
 * `ClockSkewDetected`'s `newSkewMs` now needs `todouble(customDimensions['newSkewMs'])`. If that
 * becomes painful, add an explicit `measurements` channel to {@link TelemetrySink} — do **not**
 * reinstate the two-argument form, which would only restore a guess that is wrong more often than
 * it is right. `ReactInsightsLogger` still uses that form, so the React path still splits.
 */
@Injectable()
export class ApplicationInsightsTelemetrySink implements TelemetrySink {
    private readonly ai = inject(PcpApplicationInsights) as IPcpApplicationInsights;

    trace(level: LogLevel, message: string, properties?: Record<string, unknown>): void {
        this.ai.trackTrace({
            message,
            severityLevel: AI_SEVERITY[level],
            properties: properties as Record<string, string>,
        });
    }

    event(name: string, properties?: Record<string, unknown>): void {
        this.ai.trackEvent({ name, properties: properties as Record<string, string> });
    }

    exception(error: Error, level: LogLevel, properties?: Record<string, unknown>): void {
        this.ai.trackException({
            exception: error,
            severityLevel: AI_SEVERITY[level],
            properties: properties as Record<string, string>,
        });
    }

    metric(name: string, value: number, properties?: Record<string, unknown>): void {
        this.ai.trackMetric({ name, average: value, properties: properties as Record<string, string> });
    }
}
